import { TypeBeforeAndAfter } from "@contentful/types";
import { Badge } from "@yardzen/ui/components/badge";
import { H2 } from "@yardzen/ui/components/h2";
import {
  ReactCompareSlider,
  ReactCompareSliderImage,
} from "react-compare-slider";

export function BeforeAndAfter({
  beforeAndAfter,
}: {
  beforeAndAfter: TypeBeforeAndAfter<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const beforeImagePath = beforeAndAfter.fields.before?.fields.file?.url;
  const afterImagePath = beforeAndAfter.fields.after?.fields.file?.url;
  const description = beforeAndAfter.fields.description;
  const eyebrow = beforeAndAfter.fields.eyebrow;

  // The two images are what this component is. Everything above them is
  // optional copy: an entry carrying only a before and an after is a complete
  // before-and-after, and it renders as the slider alone.
  if (!beforeImagePath || !afterImagePath) {
    return null;
  }

  // The frame follows the artwork. A flat height cropped a near-square yard
  // photo to a letterbox, so the ratio comes from the "before" asset itself.
  //
  // The tall-viewport cap is a max *width*, not a max height: capping the
  // height leaves the frame at full width, which breaks the ratio and puts
  // letterbox bars back either side of the photo (and the Before/After badges
  // out on the bare background with them). Capping the width keeps the ratio
  // exact at every size, so the image always fills its frame.
  const beforeSize = beforeAndAfter.fields.before?.fields.file?.details?.image;
  const ratio =
    beforeSize?.width && beforeSize?.height
      ? beforeSize.width / beforeSize.height
      : 3 / 2;

  // The copy shares the slider's width so the two read as one block, which is
  // why the cap lives on this wrapper rather than on the frame alone.
  const maxWidth = `calc(78vh * ${ratio.toFixed(4)})`;

  return (
    <div className="flex w-full flex-col gap-6 md:gap-8" style={{ maxWidth }}>
      <div className="relative w-full" style={{ aspectRatio: `${ratio}` }}>
        <ReactCompareSlider
          className="rounded-lg"
          style={{ height: "100%", width: "100%" }}
          itemOne={
            <ReactCompareSliderImage
              src={beforeImagePath}
              alt="Image one"
              loading="lazy"
              sizes="(max-width: 768px) 100vw, 75vw"
              style={{ objectFit: "cover", width: "100%", height: "100%" }}
            />
          }
          itemTwo={
            <ReactCompareSliderImage
              src={afterImagePath}
              alt="Image two"
              loading="lazy"
              sizes="(max-width: 768px) 100vw, 75vw"
              style={{ objectFit: "cover", width: "100%", height: "100%" }}
            />
          }
        />
        <Badge
          variant="white"
          hover={false}
          className="absolute bottom-4 left-4 text-xs"
        >
          Before
        </Badge>
        <Badge
          variant="white"
          hover={false}
          className="absolute bottom-4 right-4 text-xs"
        >
          After
        </Badge>
      </div>
      {(eyebrow || description) && (
        // A caption under the photograph, centred: the image is the subject and
        // the words describe it, which is the wrong way round when they sit on
        // top. Stacked rather than two columns, which never shared a baseline.
        <div className="flex w-full flex-col items-center gap-2 text-center">
          {eyebrow && (
            <H2 className="text-brand-secondary-dark text-xl sm:text-3xl">
              {eyebrow}
            </H2>
          )}
          {description && (
            <p className="text-brand-primary-regular max-w-2xl text-sm">
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
